import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useScrollLock } from '../../AppDrawer/hooks/useScrollLock'
import {
  SCROLL_DISMISS_PX,
  SCROLL_SHOW_TOP_PX,
  SWIPE_COMMIT_RATIO,
  clampSlidePx,
  slideDragMultiplier,
  DISMISS_TRANSITION_MS,
  DISMISS_TRANSITION_EASING,
  SNAP_BACK_TRANSITION_MS,
  SNAP_BACK_TRANSITION_EASING,
  PULL_STRIP_HEIGHT_PX,
} from '../utils'

/**
 * True when focus is in a text field inside the overlay (e.g. AI prompt).
 * Ignore window scroll sync then — mobile keyboards change scrollY and would
 * falsely trigger dismiss / snap.
 * @param {Element} overlayRoot
 * @returns {boolean}
 */
function isTextEditingFocusInsideOverlay (overlayRoot) {
  if (!overlayRoot) {
    return false
  }
  const ae = document.activeElement
  if (!ae || !overlayRoot.contains(ae)) {
    return false
  }
  const tag = typeof ae.tagName === 'string' ? ae.tagName.toLowerCase() : ''
  if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    return true
  }
  if (ae.isContentEditable) {
    return true
  }
  return false
}

/**
 * Slide / dismiss behavior for the Member Home First Touch overlay panel.
 * @param {{ showOverlay: boolean, sectionId: unknown, overlayRef: React.RefObject }} params
 */
function useMemberHomeFirstTouchSlide ({
  showOverlay,
  sectionId,
  overlayRef,
}) {
  const touchLastY = useRef(null)
  const slidePxRef = useRef(0)
  const maxSlidePxRef = useRef(-800)
  const lastWindowScrollYRef = useRef(0)
  const [slidePx, setSlidePx] = useState(0)
  const [maxSlidePx, setMaxSlidePx] = useState(-800)
  const [slideTransition, setSlideTransition] = useState(null)

  useEffect(() => {
    slidePxRef.current = slidePx
  }, [slidePx])

  useEffect(() => {
    maxSlidePxRef.current = maxSlidePx
  }, [maxSlidePx])

  useEffect(() => {
    if (!process.env.BROWSER || !showOverlay) {
      return undefined
    }
    lastWindowScrollYRef.current = window.scrollY || document.documentElement.scrollTop || 0
    const onScroll = () => {
      const y = window.scrollY || document.documentElement.scrollTop || 0
      if (isTextEditingFocusInsideOverlay(overlayRef.current)) {
        lastWindowScrollYRef.current = y
        return
      }
      const prevScrollY = lastWindowScrollYRef.current
      lastWindowScrollYRef.current = y
      const scrollTowardTop = y < prevScrollY
      const maxS = maxSlidePxRef.current
      const cur = slidePxRef.current
      const overlayNotFullyVisible = cur < -2
      if (y <= SCROLL_SHOW_TOP_PX) {
        if (overlayNotFullyVisible && scrollTowardTop) {
          setSlideTransition(`transform ${SNAP_BACK_TRANSITION_MS}ms ${SNAP_BACK_TRANSITION_EASING}`)
          slidePxRef.current = 0
          setSlidePx(0)
        }
      } else if (y > SCROLL_DISMISS_PX) {
        if (cur > maxS * 0.98) {
          setSlideTransition(`transform ${DISMISS_TRANSITION_MS}ms ${DISMISS_TRANSITION_EASING}`)
          slidePxRef.current = maxS
          setSlidePx(maxS)
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [showOverlay])

  const overlayMostlyVisible = maxSlidePx < 0 && slidePx > maxSlidePx * 0.93

  useLayoutEffect(() => {
    if (!process.env.BROWSER || !showOverlay || !overlayRef.current) {
      return undefined
    }
    const el = overlayRef.current
    const h = el.offsetHeight
    const next = -Math.abs(h * 1.2)
    setMaxSlidePx(next)
    maxSlidePxRef.current = next
    setSlidePx((prev) => {
      const clamped = clampSlidePx(prev, next)
      slidePxRef.current = clamped
      return clamped
    })
    return undefined
  }, [showOverlay, sectionId, overlayRef])

  const commitSlideEnd = useCallback(() => {
    touchLastY.current = null
    const range = Math.abs(maxSlidePx)
    const cur = slidePxRef.current
    const fractionOff = range > 0 ? Math.abs(cur) / range : 0
    if (fractionOff >= SWIPE_COMMIT_RATIO) {
      setSlideTransition(`transform ${DISMISS_TRANSITION_MS}ms ${DISMISS_TRANSITION_EASING}`)
      slidePxRef.current = maxSlidePx
      setSlidePx(maxSlidePx)
    } else {
      setSlideTransition(`transform ${SNAP_BACK_TRANSITION_MS}ms ${SNAP_BACK_TRANSITION_EASING}`)
      slidePxRef.current = 0
      setSlidePx(0)
    }
  }, [maxSlidePx])

  const onSlideTouchStart = useCallback((e) => {
    const { target } = e
    if (target instanceof Element && target.closest('.member-home-first-touch__ai')) {
      return
    }
    const [touch] = e.touches
    if (touch) {
      touchLastY.current = touch.clientY
      setSlideTransition('none')
    }
  }, [])

  const onSlideTouchMove = useCallback((e) => {
    const [touch] = e.touches
    if (touchLastY.current == null || !touch) {
      return
    }
    const { clientY } = touch
    const dy = touchLastY.current - clientY
    touchLastY.current = clientY
    const range = Math.abs(maxSlidePx)
    if (range <= 0) {
      return
    }
    setSlidePx((prev) => {
      const fractionOff = Math.abs(prev) / range
      const mult = slideDragMultiplier(fractionOff)
      const next = clampSlidePx(prev - dy * mult, maxSlidePx)
      slidePxRef.current = next
      return next
    })
  }, [maxSlidePx])

  const onSlideTouchEnd = useCallback(() => {
    if (touchLastY.current == null) {
      return
    }
    commitSlideEnd()
  }, [commitSlideEnd])

  const handleOverlayTransitionEnd = useCallback((e) => {
    if (e.propertyName !== 'transform') {
      return
    }
    setSlideTransition(null)
  }, [])

  const visuallyDismissed = maxSlidePx < 0 && slidePx <= maxSlidePx * 0.98
  const showPullStrip = maxSlidePx < 0 && slidePx <= maxSlidePx * 0.88
  const lockDocumentScroll = !!(process.env.BROWSER && showOverlay && !visuallyDismissed)

  useEffect(() => {
    if (!lockDocumentScroll) {
      return undefined
    }
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = ''
      document.body.style.overflow = ''
    }
  }, [lockDocumentScroll])

  useScrollLock({
    enabled: lockDocumentScroll,
    drawerRef: overlayRef,
  })

  const overlayStyle = {
    transform: `translateY(${slidePx}px)`,
    ...(slideTransition ? { transition: slideTransition } : {}),
  }

  return {
    overlayMostlyVisible,
    visuallyDismissed,
    showPullStrip,
    overlayStyle,
    onSlideTouchStart,
    onSlideTouchMove,
    onSlideTouchEnd,
    handleOverlayTransitionEnd,
    pullStripStyle: { height: PULL_STRIP_HEIGHT_PX },
  }
}

export default useMemberHomeFirstTouchSlide
